/**
 * Settings Component
 * 
 * Main settings dashboard with Drawer + Vertical Tabs navigation.
 * Provides consistent header with branding and theme toggle,
 * collapsible sidebar with settings sections, and main content area.
 */

// 1. External dependencies
import React, { useState, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose } from 'lucide-react';

// 2. Workspace packages
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Tabs,
  Tab,
  Button,
  useDisclosure,
  Card,
  CardBody
} from '@enfomentis/ui';

// 3. Internal absolute imports
import { AppLayout } from '@/components/shared/layouts';
import { AppSyncStatus } from '@/components/shared/sync';
import { useIsDesktop } from '@/hooks';

// 4. Relative imports
import { SettingsLayout, type BreadcrumbItemConfig } from './SettingsLayout';
import { SettingsLandingPage } from './SettingsLandingPage';
import { SettingsPlaceholder } from './SettingsPlaceholder';
import { UserAccountManagement } from './UserAccountManagement';
import { AISettingsPage } from './AI';
import { FeedbackSupportRoute } from './FeedbackSupport';
import { DataSyncSettings } from './DataSync';
import { TradingWorkflowDashboard } from './TradingWorkflow';
import { PreTradingDashboard } from './TradingWorkflow/PreTrading';
import { EconomicCalendarSettingsPage } from './TradingWorkflow/EconomicCalendar';
import { ObjectivesManagement } from './TradingWorkflow/Objectives';
import { MarketSnapshotsSettings } from './TradingWorkflow/MarketSnapshots';
import { StrategiesPlaybooksPage, ReferenceDataPage as StrategiesReferenceDataPage } from './Strategies';
import { AccountsPage, ReferenceDataPage as PortfolioReferenceDataPage, BrokersProvider } from './Portfolio';
import { MobileBrokerList, MobileBrokerCreate, MobileBrokerDetail, MobileBrokerFees, MobileFeeEditorPage, MobileInstrumentList } from './Portfolio/pages';
import { settingsSections, type SettingsSectionKey } from './settingsData';

/**
 * Strategies Settings Route Component
 *
 * Handles conditional header visibility for strategies routes:
 * - Desktop: Always shows "Trading Strategies" header
 * - Mobile at root (/settings/strategies): Shows header
 * - Mobile at nested routes (/settings/strategies/:id or deeper): Hides header (pages have own headers)
 */
interface StrategiesSettingsRouteProps {
  isDesktop: boolean;
  shouldShowPersistent: boolean;
  onOpenDrawer: () => void;
}

const StrategiesSettingsRoute: React.FC<StrategiesSettingsRouteProps> = ({
  isDesktop,
  shouldShowPersistent,
  onOpenDrawer,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect if we're on a nested mobile route (has :strategyId in path)
  // /settings/strategies = root (show header)
  // /settings/strategies/abc123 = nested (hide header on mobile)
  // /settings/strategies/abc123/playbooks/xyz789 = deeply nested (hide header on mobile)
  const path = location.pathname;
  const isLandingPage = path === '/settings/strategies' || path === '/settings/strategies/';
  const isNestedRoute = /^\/settings\/strategies\/[^/]+/.test(path);
  const hideHeaderOnMobile = !isDesktop && isNestedRoute;

  // Breadcrumbs only on landing page (nested pages handled in Phase 2)
  // Empty array = show "Settings" on mobile only, nothing on desktop
  const breadcrumbItems: BreadcrumbItemConfig[] | undefined = isLandingPage
    ? []
    : undefined;

  // Reference Data navigation buttons (desktop only, rendered in header)
  const referenceDataButtons = (
    <>
      <Card radius="none" shadow="none">
        <CardBody className="py-0 px-3">
          <div className="flex items-center justify-between">
            <Button
              size="md"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=criteria')}
            >
              Criteria
            </Button>
            <Button
              size="md"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=confluences')}
            >
              Confluences
            </Button>
            <Button
              size="md"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=entry-models')}
            >
              Entry Models
            </Button>
            <Button
              size="md"
              variant="light"
              onPress={() => navigate('/settings/strategies/reference-data?tab=categories')}
            >
              Categories
            </Button>
            </div>
        </CardBody>
      </Card>
    </>
  );

  return (
    <SettingsLayout
      title="Trading Strategies"
      description="Strategies, playbooks, criteria, opportunities"
      hideHeader={hideHeaderOnMobile}
      headerActions={referenceDataButtons}
      {...(breadcrumbItems && { breadcrumbItems })}
      {...(!shouldShowPersistent && { onOpenDrawer })}
    >
      <StrategiesPlaybooksPage />
    </SettingsLayout>
  );
};

/**
 * Portfolio Settings Route Component
 *
 * Handles conditional header visibility for portfolio routes:
 * - Desktop: Always shows "Trading Portfolio" header
 * - Mobile at root (/settings/portfolio): Shows header
 * - Mobile at nested routes (/settings/portfolio/:accountId): Hides header (pages have own headers)
 */
interface PortfolioSettingsRouteProps {
  isDesktop: boolean;
  shouldShowPersistent: boolean;
  onOpenDrawer: () => void;
}

const PortfolioSettingsRoute: React.FC<PortfolioSettingsRouteProps> = ({
  isDesktop,
  shouldShowPersistent,
  onOpenDrawer,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect if we're on a nested mobile route (has :accountId in path)
  // /settings/portfolio = root (show header)
  // /settings/portfolio/acc-1 = nested (hide header on mobile)
  const path = location.pathname;
  const isLandingPage = path === '/settings/portfolio' || path === '/settings/portfolio/';
  const isNestedRoute = /^\/settings\/portfolio\/[^/]+/.test(path);
  const hideHeaderOnMobile = !isDesktop && isNestedRoute;

  // Breadcrumbs only on landing page
  // Empty array = show "Settings" on mobile only, nothing on desktop
  const breadcrumbItems: BreadcrumbItemConfig[] | undefined = isLandingPage
    ? []
    : undefined;

  // Reference Data navigation buttons (desktop only, rendered in header)
  const referenceDataButtons = (
    <Card radius="none" shadow="none">
      <CardBody className="py-0 px-3">
        <div className="flex items-center justify-between">
          <Button
            size="md"
            variant="light"
            onPress={() => navigate('/settings/portfolio/reference-data?tab=brokers')}
          >
            Brokers
          </Button>
          <Button
            size="md"
            variant="light"
            onPress={() => navigate('/settings/portfolio/reference-data?tab=instruments')}
          >
            Instruments
          </Button>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <SettingsLayout
      title="Trading Portfolio"
      description="Accounts, risk profiles, brokers, instruments"
      hideHeader={hideHeaderOnMobile}
      headerActions={referenceDataButtons}
      {...(breadcrumbItems && { breadcrumbItems })}
      {...(!shouldShowPersistent && { onOpenDrawer })}
    >
      <AccountsPage />
    </SettingsLayout>
  );
};

/**
 * Portfolio Reference Data Route Component
 *
 * Handles desktop/mobile split for portfolio reference data:
 * - Desktop: Renders existing PortfolioReferenceDataPage (tabbed UI) unchanged
 * - Mobile: Redirects root to /brokers, renders mobile broker page tree with BrokersProvider
 *
 * Header visibility follows the Strategies pattern:
 * - Mobile at broker list root (/settings/portfolio/reference-data/brokers): Shows header
 * - Mobile at nested broker routes (new, :brokerId, deeper): Hides header (pages provide own)
 * - Desktop: Always shows header
 */
interface PortfolioReferenceDataRouteProps {
  isDesktop: boolean;
  shouldShowPersistent: boolean;
  onOpenDrawer: () => void;
}

const PortfolioReferenceDataRoute: React.FC<PortfolioReferenceDataRouteProps> = ({
  isDesktop,
  shouldShowPersistent,
  onOpenDrawer,
}) => {
  // Mobile: always hide SettingsLayout header — list roots provide Select header,
  // nested broker routes provide their own back-nav headers
  const hideHeaderOnMobile = !isDesktop;

  const breadcrumbItems: BreadcrumbItemConfig[] = [{ label: 'Portfolio', href: '/settings/portfolio' }];

  return (
    <SettingsLayout
      title="Reference Data"
      breadcrumbItems={breadcrumbItems}
      hideHeader={hideHeaderOnMobile}
      {...(!shouldShowPersistent && { onOpenDrawer })}
    >
      {isDesktop ? (
        <PortfolioReferenceDataPage />
      ) : (
        <BrokersProvider>
          <Routes>
            <Route index element={<Navigate to="brokers" replace />} />
            <Route path="brokers" element={<MobileBrokerList />} />
            <Route path="brokers/new" element={<MobileBrokerCreate />} />
            <Route path="brokers/:brokerId" element={<MobileBrokerDetail />} />
            <Route path="brokers/:brokerId/fees" element={<MobileBrokerFees />} />
            <Route path="brokers/:brokerId/fees/:nodeId" element={<MobileFeeEditorPage />} />
            <Route path="brokers/:brokerId/*" element={<Navigate to="fees" replace />} />
            <Route path="instruments" element={<MobileInstrumentList />} />
            <Route path="*" element={<Navigate to="brokers" replace />} />
          </Routes>
        </BrokersProvider>
      )}
    </SettingsLayout>
  );
};

/**
 * Main Settings Component
 *
 * Features:
 * - Consistent header with Dashboard branding and navigation
 * - Collapsible sidebar with vertical tabs for settings sections
 * - Mobile-responsive with hamburger drawer behavior
 * - Placeholder content for all settings sections
 * - Theme integration with app-level theme management
 */
export const Settings: React.FC = () => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();

  // Type for active section - includes main sections and landing
  type ActiveSection = SettingsSectionKey | 'landing';

  // Derive active section from URL path for sidebar highlighting
  // Returns 'landing' for root /settings path (no sidebar highlight)
  const getActiveSectionFromPath = (): ActiveSection => {
    const path = location.pathname;
    if (path === '/settings' || path === '/settings/') return 'landing';
    if (path.startsWith('/settings/portfolio')) return 'portfolio';
    if (path.startsWith('/settings/profile')) return 'profile';
    if (path.startsWith('/settings/data-sync')) return 'data-sync';
    if (path.startsWith('/settings/workflow')) return 'workflow';
    if (path.startsWith('/settings/strategies')) return 'strategies';
    if (path.startsWith('/settings/preferences')) return 'preferences';
    if (path.startsWith('/settings/ai')) return 'ai';
    if (path.startsWith('/settings/feedback-support')) return 'feedback-support';
    return 'landing';
  };

  // Memoize valid section keys for sidebar highlighting
  const validSectionKeys = useMemo(
    () => new Set(settingsSections.map(s => s.key)),
    []
  );

  // Map sub-route keys to parent tab keys for sidebar highlighting
  const getTabHighlightKey = (section: ActiveSection): SettingsSectionKey | null => {
    if (section === 'landing') return null;
    return validSectionKeys.has(section as SettingsSectionKey) ? (section as SettingsSectionKey) : null;
  };

  const activeSection = getActiveSectionFromPath();

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    // Default to open on desktop, closed on mobile (SSR-safe)
    return typeof window !== 'undefined' && window.innerWidth >= 768;
  });

  // Desktop detection
  const isDesktop = useIsDesktop();

  const handleSectionChange = useCallback((key: React.Key): void => {
    const sectionKey = String(key);

    // Map section keys to route paths
    const routeMap: Record<string, string> = {
      'preferences': '/settings/preferences',
      'workflow': '/settings/workflow',
      'strategies': '/settings/strategies',
      'portfolio': '/settings/portfolio',
      'data-sync': '/settings/data-sync',
      'ai': '/settings/ai',
      'feedback-support': '/settings/feedback-support',
      'profile': '/settings/profile'
    };

    navigate(routeMap[sectionKey] || '/settings/preferences');

    // Auto-close drawer on mobile only (desktop sidebar stays open)
    if (!isDesktop && isOpen) {
      onOpenChange();
    }
  }, [navigate, isDesktop, isOpen, onOpenChange]);

  const handleSidebarToggle = useCallback((): void => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  // Unified handler for opening sidebar/drawer
  const handleOpenSidebar = useCallback((): void => {
    if (isDesktop) {
      setIsSidebarOpen(true);
    } else {
      onOpen();
    }
  }, [isDesktop, onOpen]);

  // Handler for landing page card navigation - type-safe wrapper for handleSectionChange
  const handleLandingNavigation = useCallback((sectionKey: SettingsSectionKey): void => {
    handleSectionChange(sectionKey);
  }, [handleSectionChange]);

  // Desktop: show persistent sidebar when open. Mobile: never show persistent (uses drawer)
  const shouldShowPersistent = isSidebarOpen && isDesktop;

  // Map active section to valid tab key (handles sub-routes like portfolio-instruments)
  const tabSelectedKey = getTabHighlightKey(activeSection);

  // settingsSections is now exported at module level for sharing with SettingsLandingPage

  return (
    <AppLayout
      showBackButton={true}
      backHref="/dashboard"
      headerContent={<AppSyncStatus />}
    >
      <div className="flex flex-row h-full ">
          {/* Persistent Sidebar (Desktop + Open) - hidden on landing page */}
          {shouldShowPersistent && activeSection !== 'landing' && (
            <div className="w-80 border-r  border-default-200 bg-transparent shrink-0 h-full">
              <div className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Settings</h2>
                  <Button
                    variant="light"
                    size="sm"
                    isIconOnly
                    onPress={handleSidebarToggle}
                    title="Collapse Settings Menu"
                    aria-label="Collapse Settings Menu"
                    className="text-text-secondary"
                  >
                    <PanelLeftClose size={16} />
                  </Button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                  <Tabs
                    isVertical
                    selectedKey={tabSelectedKey}
                    onSelectionChange={handleSectionChange}
                    variant="light"
                    color="primary"
                    className="w-full h-full"
                    classNames={{
                      tabList: "w-full gap-2",
                      tab: "w-full justify-start p-4 h-auto",
                      tabContent: "w-full text-left",
                      cursor: "bg-default-200"
                    }}
                  >
                    {settingsSections.map((section) => (
                      <Tab 
                        key={section.key} 
                        title={
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-medium text-text-primary">{section.title}</span>
                            <span className="text-sm text-text-secondary text-wrap">
                              {section.description}
                            </span>
                          </div>
                        }
                      />
                    ))}
                  </Tabs>
                </div>
              </div>
            </div>
          )}

          {/* Settings Drawer (Mobile only) - hidden on landing page */}
          {!isDesktop && activeSection !== 'landing' && (
            <Drawer
              isOpen={isOpen}
              onOpenChange={onOpenChange}
              placement="bottom"
              size="md"
            >
              <DrawerContent>
                <DrawerHeader>
                  <h2 className="text-xl font-semibold">Settings</h2>
                </DrawerHeader>
                <DrawerBody className="p-0">
                  <Tabs
                    isVertical
                    selectedKey={tabSelectedKey}
                    onSelectionChange={handleSectionChange}
                    variant="light"
                    color="primary"
                    className="w-full"
                    classNames={{
                      tabList: "w-full gap-2 px-4",
                      tab: "w-full justify-start p-4 h-auto",
                      tabContent: "w-full text-left",
                      cursor: "bg-default-200"
                    }}
                  >
                    {settingsSections.map((section) => (
                      <Tab
                        key={section.key}
                        title={
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-medium text-text-primary">{section.title}</span>
                            <span className="text-sm text-text-secondary text-wrap">
                              {section.description}
                            </span>
                          </div>
                        }
                      />
                    ))}
                  </Tabs>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          )}

          {/* Main Settings Content */}
          <div className="flex-1 min-h-0 p-1 box-border overflow-x-hidden">
            <Routes>
              {/* Index route - Settings Landing Page (no onOpenDrawer - nav hidden on landing) */}
              <Route index element={
                <SettingsLayout
                  title="Settings"
                  description="Configure your trading journal and application preferences"
                >
                  <SettingsLandingPage onNavigateToSection={handleLandingNavigation} />
                </SettingsLayout>
              } />

              {/* App Preferences - explicit route */}
              <Route path="preferences" element={
                <SettingsLayout
                  title="App Preferences"
                  description="Language, timezone, trading schedule"
                  breadcrumbItems={[]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <SettingsPlaceholder section="app-preferences" />
                </SettingsLayout>
              } />

              {/* Trading Workflow Dashboard */}
              <Route path="workflow" element={
                <SettingsLayout
                  title="Trading Workflow"
                  description="Pre-trading, live trading, post-trading settings"
                  breadcrumbItems={[]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <TradingWorkflowDashboard />
                </SettingsLayout>
              } />

              {/* Pre-Trading Dashboard */}
              <Route path="workflow/pre-trading" element={
                <SettingsLayout
                  title="Pre-Trading Settings"
                  description="Configure your pre-trading ritual"
                  showBackButton={true}
                  onBack={() => navigate('/settings/workflow')}
                  breadcrumbItems={[{ label: 'Workflow', href: '/settings/workflow' }]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <PreTradingDashboard />
                </SettingsLayout>
              } />

              {/* Objectives Management */}
              <Route path="workflow/objectives" element={
                <SettingsLayout
                  title="Objectives Library"
                  description="Manage your trading objectives"
                  showBackButton={true}
                  onBack={() => navigate('/settings/workflow')}
                  breadcrumbItems={[{ label: 'Workflow', href: '/settings/workflow' }]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <ObjectivesManagement />
                </SettingsLayout>
              } />

              {/* Market Snapshots Settings */}
              <Route path="workflow/market-snapshots" element={
                <SettingsLayout
                  title="Market Snapshots"
                  description="Configure fields for capturing market conditions"
                  showBackButton={true}
                  onBack={() => navigate('/settings/workflow')}
                  breadcrumbItems={[{ label: 'Workflow', href: '/settings/workflow' }]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <MarketSnapshotsSettings />
                </SettingsLayout>
              } />

              {/* Economic Calendar Settings */}
              <Route path="workflow/economic-calendar" element={
                <SettingsLayout
                  title="Economic Calendar"
                  description="Configure economic event tracking and filtering"
                  showBackButton={true}
                  onBack={() => navigate('/settings/workflow')}
                  breadcrumbItems={[{ label: 'Workflow', href: '/settings/workflow' }]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <EconomicCalendarSettingsPage />
                </SettingsLayout>
              } />

              {/* Reference Data - must be before strategies/* wildcard */}
              <Route path="strategies/reference-data" element={
                <SettingsLayout
                  title="Reference Data"
                  description="Manage criteria, confluences, and entry models"
                  breadcrumbItems={[{ label: 'Strategies', href: '/settings/strategies' }]}
                  hideHeader={!isDesktop}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <StrategiesReferenceDataPage />
                </SettingsLayout>
              } />

              {/* Trading Strategies - wildcard to support mobile nested routes */}
              <Route path="strategies/*" element={
                <StrategiesSettingsRoute
                  isDesktop={isDesktop}
                  shouldShowPersistent={shouldShowPersistent}
                  onOpenDrawer={handleOpenSidebar}
                />
              } />

              {/* Trading Portfolio - Reference Data (must be before portfolio/* wildcard) */}
              <Route path="portfolio/reference-data/*" element={
                <PortfolioReferenceDataRoute
                  isDesktop={isDesktop}
                  shouldShowPersistent={shouldShowPersistent}
                  onOpenDrawer={handleOpenSidebar}
                />
              } />

              {/* Trading Portfolio - wildcard to support mobile nested routes */}
              <Route path="portfolio/*" element={
                <PortfolioSettingsRoute
                  isDesktop={isDesktop}
                  shouldShowPersistent={shouldShowPersistent}
                  onOpenDrawer={handleOpenSidebar}
                />
              } />

              {/* Data & Sync */}
              <Route path="data-sync" element={
                <SettingsLayout
                  title="Data & Sync"
                  description="Storage location, sync settings, device management"
                  breadcrumbItems={[]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <DataSyncSettings />
                </SettingsLayout>
              } />

              {/* AI Assistant */}
              <Route path="ai" element={
                <SettingsLayout
                  title="AI Assistant"
                  description="AI provider, API key, model configuration"
                  breadcrumbItems={[]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <AISettingsPage />
                </SettingsLayout>
              } />

              {/* Feedback & Support */}
              <Route path="feedback-support" element={
                <FeedbackSupportRoute
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                />
              } />

              {/* Profile Settings */}
              <Route path="profile" element={
                <SettingsLayout
                  title="Profile Settings"
                  description="User profile, license, data export/import"
                  breadcrumbItems={[]}
                  {...(!shouldShowPersistent && { onOpenDrawer: handleOpenSidebar })}
                >
                  <UserAccountManagement />
                </SettingsLayout>
              } />

              {/* Catch-all: redirect unknown paths to settings landing page */}
              <Route path="*" element={<Navigate to="/settings" replace />} />
            </Routes>
        </div>
      </div>
    </AppLayout>
  );
};